import type { AbsTask } from '@core/transmission/AbsTask'
import { isEmpty } from 'lodash'
import { TaskState } from './ITask'

export class TaskPool<T extends AbsTask> {
  #list = new Queue<T>()

  get size(): number {
    return this.#list.size
  }

  add(task: T) {
    this.#list.enqueue(task)
  }

  remove(task: T): void {
    this.#list.remove(task)
  }

  getAllTask(): T[] {
    return this.#list.items.sort((a, b) => b.sequenceId - a.sequenceId)
  }

  getOnGoingTasks(): T[] {
    return this.#list.items.filter((item) => item.state === TaskState.GOING)
  }

  getOnGoingTaskCount(): number {
    return this.getOnGoingTasks().length
  }

  getAllPausedTasks(): T[] {
    return this.#list.items.filter((task) => task.state === TaskState.PAUSE).reverse()
  }

  getAllGoingAndWaitingTasks(): T[] {
    return this.#list.items.filter((task) => task.state === TaskState.GOING || task.state === TaskState.WAITING)
  }

  getNextTask(): T | undefined {
    let waitingTask = this.#list.items.filter((item) => item.state === TaskState.WAITING)

    waitingTask = waitingTask.sort((a, b) => b.sequenceId - a.sequenceId)

    if (isEmpty(waitingTask)) return undefined

    return waitingTask.pop()
  }

  includesTaskByState(states: TaskState[]): boolean {
    return this.#list.items.some((task) => states.includes(task.state))
  }
}

class Queue<T extends AbsTask> {
  #items: T[] = []

  get items(): T[] {
    return this.#items
  }

  enqueue(task: T): boolean {
    this.#items = this.#items.filter((item) => item.id !== task.id)
    this.#items.push(task)

    return true
  }

  dequeue(): T | undefined {
    return this.#items.shift()
  }

  /**
   * first item in queue
   * @returns
   */
  front(): T | undefined {
    return this.#items[0]
  }

  isEmpty(): boolean {
    return this.#items.length === 0
  }

  includes(task: T): boolean {
    return this.#items.includes(task)
  }

  remove(task: T): void {
    this.#items = this.#items.filter((item) => item !== task)
  }

  get size(): number {
    return this.#items.length
  }
}
